<template>
  <div class="qe" :class="{ 'qe--disabled': disabled }">
    <div class="qe__toolbar" v-if="editor">
      <button type="button" class="qe__btn" :class="{ 'qe__btn--on': editor.isActive('bold') }" title="Fett" @click="editor.chain().focus().toggleBold().run()"><v-icon small>mdi-format-bold</v-icon></button>
      <button type="button" class="qe__btn" :class="{ 'qe__btn--on': editor.isActive('italic') }" title="Kursiv" @click="editor.chain().focus().toggleItalic().run()"><v-icon small>mdi-format-italic</v-icon></button>
      <span class="qe__sep"></span>
      <button type="button" class="qe__btn" :class="{ 'qe__btn--on': editor.isActive('bulletList') }" title="Aufzählung" @click="editor.chain().focus().toggleBulletList().run()"><v-icon small>mdi-format-list-bulleted</v-icon></button>
      <button type="button" class="qe__btn" :class="{ 'qe__btn--on': editor.isActive('orderedList') }" title="Nummerierung" @click="editor.chain().focus().toggleOrderedList().run()"><v-icon small>mdi-format-list-numbered</v-icon></button>
      <span class="qe__sep"></span>
      <button type="button" class="qe__btn" :class="{ 'qe__btn--on': editor.isActive('link') }" title="Link" @click="setLink"><v-icon small>mdi-link-variant</v-icon></button>
      <button v-if="onAi" type="button" class="qe__btn qe__btn--ai" title="Mit KI optimieren" :disabled="aiBusy || isEmpty" @click="runAi">
        <v-progress-circular v-if="aiBusy" indeterminate size="14" width="2" color="#7b5cd6" />
        <v-icon v-else small color="#7b5cd6">mdi-creation</v-icon>
      </button>
      <span class="qe__spacer"></span>
      <button type="button" class="qe__btn" title="Rückgängig" :disabled="!editor.can().undo()" @click="editor.chain().focus().undo().run()"><v-icon small>mdi-undo</v-icon></button>
      <button type="button" class="qe__btn" title="Wiederholen" :disabled="!editor.can().redo()" @click="editor.chain().focus().redo().run()"><v-icon small>mdi-redo</v-icon></button>
    </div>
    <EditorContent :editor="editor" class="qe__content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";

// Antwort-Editor wie in Quelp (TipTap): Fett, Kursiv, Listen, Link, KI, Undo/Redo.
// v-model = HTML.
export default {
  name: "QuelpEditor",
  components: { EditorContent },
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    onAi: { type: Function, default: null }, // (html) => Promise<html>
  },
  data() {
    return { editor: null, aiBusy: false };
  },
  computed: {
    isEmpty() {
      return !this.editor || this.editor.isEmpty;
    },
  },
  watch: {
    value(v) {
      if (!this.editor) return;
      if (v === this.editor.getHTML()) return;
      this.editor.commands.setContent(v || "", false);
    },
    disabled(v) {
      if (this.editor) this.editor.setEditable(!v);
    },
  },
  mounted() {
    this.editor = new Editor({
      content: this.value || "",
      editable: !this.disabled,
      extensions: [
        StarterKit.configure({ heading: false, codeBlock: false, blockquote: false, horizontalRule: false }),
        Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
        Placeholder.configure({ placeholder: this.placeholder }),
      ],
      onUpdate: ({ editor }) => {
        const html = editor.isEmpty ? "" : editor.getHTML();
        this.$emit("input", html);
      },
    });
  },
  beforeDestroy() {
    if (this.editor) this.editor.destroy();
  },
  methods: {
    focus() {
      if (this.editor) this.editor.commands.focus("end");
    },
    setLink() {
      const prev = this.editor.getAttributes("link").href || "";
      const url = window.prompt("Link-Adresse", prev || "https://");
      if (url === null) return;
      if (!url.trim()) {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
    },
    async runAi() {
      if (!this.onAi || this.isEmpty) return;
      this.aiBusy = true;
      try {
        const html = await this.onAi(this.editor.getHTML());
        if (html) {
          this.editor.commands.setContent(html, true);
          this.$emit("input", this.editor.getHTML());
        }
      } finally {
        this.aiBusy = false;
      }
    },
  },
};
</script>

<style lang="scss">
.qe {
  border: 1px solid rgba(0, 0, 0, 0.16);
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
  &:focus-within { border-color: rgba(123, 92, 214, 0.6); box-shadow: 0 0 0 3px rgba(123, 92, 214, 0.12); }
  &--disabled { opacity: 0.6; }
  &__toolbar { display: flex; align-items: center; gap: 2px; padding: 4px 6px; border-bottom: 1px solid rgba(0, 0, 0, 0.06); background: #fafafa; }
  &__btn {
    border: 0; background: transparent; border-radius: 8px; width: 30px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: rgba(0, 0, 0, 0.6);
    &:hover { background: rgba(0, 0, 0, 0.05); }
    &--on { background: #edeafa; .v-icon { color: #6a4fc4 !important; } }
    &:disabled { opacity: 0.35; cursor: default; }
    .v-icon { font-size: 18px !important; }
  }
  &__sep { width: 1px; height: 18px; background: rgba(0, 0, 0, 0.1); margin: 0 4px; }
  &__spacer { flex: 1 1 auto; }
  &__content { min-height: 0; }
  &__content {
    .ProseMirror {
      min-height: 120px; max-height: 38vh; overflow-y: auto; padding: 10px 12px; font-size: 13.5px; line-height: 1.5; color: #282828; outline: none;
      p { margin: 0 0 8px; }
      ul, ol { padding-left: 20px; margin: 0 0 8px; }
      a { color: #6a4fc4; text-decoration: underline; }
      p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: rgba(0, 0, 0, 0.35); float: left; height: 0; pointer-events: none; }
    }
  }
}
</style>
